// RB-MY01-PalmGarden.web/src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../services/api';
import { 
  Typography, Box, Paper, Card, CardContent, 
  CardActions, Button, CircularProgress, CardMedia,
  Chip, Divider, Avatar, IconButton, Tooltip,
  useTheme, useMediaQuery, Grid, Stack, Badge,
  LinearProgress, Menu, MenuItem
} from '@mui/material';
import { Link } from 'react-router-dom';
import { GridContainer, GridItem } from '../components/SimpleGrid';
import SearchIcon from '@mui/icons-material/Search';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import CategoryIcon from '@mui/icons-material/Category';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import NotificationsIcon from '@mui/icons-material/Notifications';

// Admin dashboard summary types
interface AdminDashboardSummary {
  totalPlants: number;
  publishedPlants: number;
  unpublishedCount: number;
  totalCategories: number;
  recentlyAddedPlants: PlantEntry[];
  unpublishedPlants: PlantEntry[];
}

interface PlantEntry {
  id: number;
  scientificName: string;
  commonName: string;
  family: string;
  category: string;
  imageUrl: string;
  isPublished: boolean;
  lastUpdated: string;
  imagesCount: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const api = useApi();
  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [activeSort, setActiveSort] = useState<string>('Newest');
  
  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Mock data for now - replace with actual API call
      const mockData: AdminDashboardSummary = {
        totalPlants: 156,
        publishedPlants: 124,
        unpublishedCount: 32,
        totalCategories: 18,
        recentlyAddedPlants: [
          {
            id: 1,
            scientificName: "Heliconia rostrata",
            commonName: "Lobster Claw",
            family: "Heliconiaceae",
            category: "Tropical",
            imageUrl: "https://images.unsplash.com/photo-1596720226373-66943c81db7b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80",
            isPublished: true,
            lastUpdated: "2023-05-15T10:30:00Z",
            imagesCount: 5
          },
          {
            id: 2,
            scientificName: "Strelitzia reginae",
            commonName: "Bird of Paradise",
            family: "Strelitziaceae",
            category: "Flowering",
            imageUrl: "https://images.unsplash.com/photo-1596636478939-6d15079d5f9e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=400&q=80",
            isPublished: true,
            lastUpdated: "2023-05-14T14:45:00Z",
            imagesCount: 3
          },
          {
            id: 3,
            scientificName: "Rafflesia arnoldii",
            commonName: "Corpse Lily",
            family: "Rafflesiaceae",
            category: "Parasitic",
            imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Rafflesia_arnoldii_-_Botanical_Garden_in_Bogor.jpg/800px-Rafflesia_arnoldii_-_Botanical_Garden_in_Bogor.jpg",
            isPublished: false,
            lastUpdated: "2023-05-12T09:15:00Z",
            imagesCount: 2
          }
        ],
        unpublishedPlants: [
          {
            id: 3,
            scientificName: "Rafflesia arnoldii",
            commonName: "Corpse Lily",
            family: "Rafflesiaceae",
            category: "Parasitic",
            imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Rafflesia_arnoldii_-_Botanical_Garden_in_Bogor.jpg/800px-Rafflesia_arnoldii_-_Botanical_Garden_in_Bogor.jpg",
            isPublished: false,
            lastUpdated: "2023-05-12T09:15:00Z",
            imagesCount: 2
          },
          {
            id: 4,
            scientificName: "Amorphophallus titanum",
            commonName: "Titan Arum",
            family: "Araceae",
            category: "Flowering",
            imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Titan-arum1web.jpg/800px-Titan-arum1web.jpg",
            isPublished: false,
            lastUpdated: "2023-05-10T11:20:00Z",
            imagesCount: 1
          },
          {
            id: 5,
            scientificName: "Nepenthes rajah",
            commonName: "Kinabalu Pitcher Plant",
            family: "Nepenthaceae",
            category: "Carnivorous",
            imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e6/Nepenthes_rajah_3.jpg/800px-Nepenthes_rajah_3.jpg",
            isPublished: false,
            lastUpdated: "2023-05-08T16:40:00Z",
            imagesCount: 4
          }
        ]
      };
      
      setSummary(mockData);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Test API connection
  const testApiConnection = async () => {
    try {
      const response = await fetch('/api/health');
      const data = await response.json();
      alert(`API Status: ${data.status}`);
    } catch (error) {
      alert(`API Error: ${error}`);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleFilterOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = (filter?: string) => {
    if (filter) {
      setActiveFilter(filter);
    }
    setFilterAnchorEl(null);
  };
  
  const handleSortOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };
  
  const handleSortClose = (sort?: string) => {
    if (sort) {
      setActiveSort(sort);
    }
    setSortAnchorEl(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Admin Dashboard Header - Enhanced */}
      <Paper 
        elevation={3}
        sx={{ 
          p: { xs: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1a5632 0%, #2e7d32 100%)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          color: 'white' 
        }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontSize: { xs: '1.75rem', md: '2.125rem' }
            }}>
              <DashboardIcon sx={{ mr: 1 }} />
              Plant Database Admin
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
              Manage and publish plant information to the public site
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            mt: { xs: 2, md: 0 },
            alignSelf: { xs: 'stretch', md: 'auto' }
          }}>
            <Tooltip title="Notifications">
              <IconButton 
                color="inherit" 
                sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.1)' }}
              >
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Button 
              variant="contained" 
              component={Link}
              to="/plants/add"
              startIcon={<AddIcon />}
              sx={{ 
                bgcolor: 'white', 
                color: 'primary.dark',
                '&:hover': { bgcolor: '#f5f5f5' },
                fontWeight: 'bold'
              }}
            >
              Add New Plant
            </Button>
          </Box>
        </Box>
        
        {/* Progress bar showing database completeness */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="white">
              Database Completeness
            </Typography>
            <Typography variant="body2" color="white">
              {Math.round((summary?.publishedPlants || 0) / (summary?.totalPlants || 1) * 100)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(summary?.publishedPlants || 0) / (summary?.totalPlants || 1) * 100}
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white'
              }
            }}
          />
        </Box>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 3, borderRadius: 2 }}>
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchDashboardData}
            startIcon={<RefreshIcon />}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      ) : (
        <Box>
          {/* Stats Cards */}
          <GridContainer spacing={3} sx={{ mb: 3 }}>
            <GridItem xs={6} sm={3}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <LocalFloristIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary?.totalPlants || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Plants
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={6} sm={3}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <VisibilityIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary?.publishedPlants}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Published Plants
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={6} sm={3}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <PublishIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary?.unpublishedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unpublished Plants
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={6} sm={3}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <CategoryIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary?.totalCategories}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Categories
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </GridItem>
          </GridContainer>
          
          {/* Quick Actions Panel - Enhanced with icons and better spacing */}
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="h6" component="h2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <IconButton size="small" sx={{ mr: 1, bgcolor: 'primary.light', color: 'white' }}>
                <DashboardIcon fontSize="small" />
              </IconButton>
              Quick Actions
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{
                '& .MuiButton-root': {
                  justifyContent: 'flex-start',
                  py: 1.5,
                  borderRadius: 2,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  }
                }
              }}
            >
              <Button 
                variant="outlined" 
                component={Link} 
                to="/plants/add" 
                startIcon={<AddIcon />}
              >
                Add New Plant
              </Button>
              <Button 
                variant="outlined" 
                component={Link} 
                to="/plants/manage" 
                startIcon={<EditIcon />}
              >
                Manage Plants
              </Button>
              <Button 
                variant="outlined" 
                component={Link} 
                to="/categories" 
                startIcon={<CategoryIcon />}
              >
                Manage Categories
              </Button>
              <Button 
                variant="outlined" 
                component={Link} 
                to="/images" 
                startIcon={<PhotoLibraryIcon />}
              >
                Image Gallery
              </Button>
            </Stack>
          </Paper>
          
          {/* Unpublished Plants - Add filtering and sorting */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}>
              <Typography variant="h5" component="h2" sx={{ mb: { xs: 1, sm: 0 } }}>
                Unpublished Plants
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'space-between', sm: 'flex-end' }
              }}>
                <Button 
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterOpen}
                  sx={{ textTransform: 'none' }}
                >
                  {activeFilter}
                </Button>
                <Menu
                  anchorEl={filterAnchorEl}
                  open={Boolean(filterAnchorEl)}
                  onClose={() => handleFilterClose()}
                >
                  <MenuItem onClick={() => handleFilterClose('All')}>All</MenuItem>
                  <MenuItem onClick={() => handleFilterClose('Tropical')}>Tropical</MenuItem>
                  <MenuItem onClick={() => handleFilterClose('Flowering')}>Flowering</MenuItem>
                  <MenuItem onClick={() => handleFilterClose('Carnivorous')}>Carnivorous</MenuItem>
                </Menu>
                
                <Button 
                  size="small"
                  startIcon={<SortIcon />}
                  onClick={handleSortOpen}
                  sx={{ textTransform: 'none' }}
                >
                  {activeSort}
                </Button>
                <Menu
                  anchorEl={sortAnchorEl}
                  open={Boolean(sortAnchorEl)}
                  onClose={() => handleSortClose()}
                >
                  <MenuItem onClick={() => handleSortClose('Newest')}>Newest</MenuItem>
                  <MenuItem onClick={() => handleSortClose('Oldest')}>Oldest</MenuItem>
                  <MenuItem onClick={() => handleSortClose('A-Z')}>A-Z</MenuItem>
                  <MenuItem onClick={() => handleSortClose('Z-A')}>Z-A</MenuItem>
                </Menu>
                
                <Button 
                  component={Link} 
                  to="/plants/unpublished"
                  endIcon={<ArrowForwardIcon />}
                  variant="outlined"
                  size="small"
                >
                  View All
                </Button>
              </Box>
            </Box>
            
            {/* Enhanced plant cards with more options */}
            <GridContainer spacing={3}>
              {summary?.unpublishedPlants.map(plant => (
                <GridItem xs={12} sm={6} md={4} key={plant.id}>
                  <Card sx={{ 
                    display: 'flex',
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    },
                    position: 'relative'
                  }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 120, objectFit: 'cover' }}
                      image={plant.imageUrl}
                      alt={plant.commonName}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <CardContent sx={{ flex: '1 0 auto', pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="subtitle2" component="div" fontStyle="italic">
                            {plant.scientificName}
                          </Typography>
                          <IconButton 
                            size="small" 
                            sx={{ ml: 1, mt: -0.5, mr: -0.5 }}
                            onClick={handleMenuOpen}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body1" component="div" fontWeight="medium">
                          {plant.commonName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                          {plant.family}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <PhotoLibraryIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {plant.imagesCount} images
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Updated: {formatDate(plant.lastUpdated)}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          component={Link} 
                          to={`/plants/${plant.id}/edit`}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small"
                          color="success"
                          startIcon={<PublishIcon />}
                        >
                          Publish
                        </Button>
                      </CardActions>
                    </Box>
                  </Card>
                </GridItem>
              ))}
            </GridContainer>
          </Paper>
          
          {/* Recently Added Plants */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Recently Added
              </Typography>
              <Button 
                component={Link} 
                to="/plants"
                endIcon={<ArrowForwardIcon />}
              >
                View All Plants
              </Button>
            </Box>
            
            <GridContainer spacing={3}>
              {summary?.recentlyAddedPlants.map(plant => (
                <GridItem xs={12} sm={6} md={4} key={plant.id}>
                  <Card sx={{ 
                    display: 'flex',
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    },
                    border: plant.isPublished ? 'none' : '1px solid #f44336'
                  }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 120, objectFit: 'cover' }}
                      image={plant.imageUrl}
                      alt={plant.commonName}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <CardContent sx={{ flex: '1 0 auto', pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle2" component="div" fontStyle="italic">
                              {plant.scientificName}
                            </Typography>
                            <Typography variant="body1" component="div" fontWeight="medium">
                              {plant.commonName}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={plant.isPublished ? "Published" : "Draft"} 
                            color={plant.isPublished ? "success" : "warning"}
                            sx={{ height: 24 }}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" component="div">
                          {plant.family}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <PhotoLibraryIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {plant.imagesCount} images
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Added: {formatDate(plant.lastUpdated)}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          component={Link} 
                          to={`/plants/${plant.id}/edit`}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small"
                          component={Link}
                          to={`/plants/${plant.id}`}
                          startIcon={<VisibilityIcon />}
                        >
                          View
                        </Button>
                      </CardActions>
                    </Box>
                  </Card>
                </GridItem>
              ))}
            </GridContainer>
          </Paper>
        </Box>
      )}
      
      {/* Context menu for plant actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>Quick Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Duplicate</MenuItem>
        <MenuItem onClick={handleMenuClose}>Preview</MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};

export default Dashboard;